import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from './global-environment';
import { AuthService } from './auth.service';

/**
 * 元件權限資料結構（從 API 回傳）
 */
export interface ComponentPermissionData {
  componentName: string;
  hasAccess: boolean;
  permissions: string[];      // ['view', 'create', 'edit', 'delete', 'disable', 'export', etc.]
  dataScope?: string;         // 'all' | 'own' | 'department-self' | 'department-below'
  userRoles: string[];        // 使用者的角色列表（支援多角色）
}

/**
 * API 回應格式
 */
interface PermissionApiResponse {
  success: boolean;
  data: ComponentPermissionData;
  error?: string;
  message?: string;
}

/**
 * 元件權限服務
 *
 * 簡化版本：直接呼叫後端 API 取得已解析的權限資料
 *
 * 使用方式：
 * 1. 在元件中注入 ComponentPermissionService
 * 2. 在 ngOnInit 中呼叫 getPermission()
 * 3. 使用回傳的權限資料來控制 UI 和功能
 */
@Injectable({
  providedIn: 'root'
})
export class ComponentPermissionService {

  private apiUrl = environment.apiUrl;

  constructor(
    private http: HttpClient,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {}

  /**
   * 取得元件權限資料
   *
   * @param componentName 元件名稱（可選，預設使用當前元件類別名稱）
   * @returns 權限資料，如果取得失敗則返回 null
   *
   * @example
   * const permission = await this.permissionService.getPermission('showlistComponent');
   * if (permission?.hasAccess) {
   *   // 有存取權限
   *   if (permission.permissions.includes('edit')) {
   *     // 顯示編輯按鈕
   *   }
   * }
   */
  async getPermission(componentName?: string): Promise<ComponentPermissionData | null> {
    try {
      // 從 URL 取得 assistantNo
      const assistantNo = this.route.snapshot.queryParams['id'];
      if (!assistantNo) {
        console.warn('找不到 assistantNo，無法取得權限');
        return null;
      }

      // 使用 AuthService 取得使用者在該模組的角色
      const userRoles = this.authService.getUserRolesForModule(assistantNo);
      if (!userRoles || userRoles.length === 0) {
        console.warn('找不到使用者角色，無法取得權限');
        return null;
      }

      // 將角色陣列轉換為逗號分隔的字串
      const userRolesString = userRoles.join(',');

      // 如果沒有指定元件名稱，使用類別名稱
      const finalComponentName = componentName || this.getCallerComponentName();

      // 呼叫 API（支援多角色）
      const url = `${this.apiUrl}/api/permissions/component/resolved`;
      const params = {
        companyid: environment.companyid,
        assistantNo: assistantNo,
        componentName: finalComponentName,
        userRoles: userRolesString  // 傳送逗號分隔的角色字串
      };

      const response = await firstValueFrom(
        this.http.get<PermissionApiResponse>(url, { params })
      );

      if (response && response.success && response.data) {
        console.log('元件權限資料:', response.data);
        return response.data;
      }

      return null;
    } catch (error) {
      console.error('取得元件權限時發生錯誤:', error);
      return null;
    }
  }

  /**
   * 檢查是否有特定權限
   *
   * @param permission 權限資料
   * @param requiredPermission 需要的權限（例如 'edit', 'delete'）
   * @returns 是否有該權限
   */
  hasPermission(permission: ComponentPermissionData | null, requiredPermission: string): boolean {
    if (!permission) return false;
    return permission.permissions.includes(requiredPermission);
  }

  /**
   * 檢查是否有任一權限
   *
   * @param permission 權限資料
   * @param requiredPermissions 需要的權限列表
   * @returns 是否有任一權限
   */
  hasAnyPermission(permission: ComponentPermissionData | null, requiredPermissions: string[]): boolean {
    if (!permission) return false;
    return requiredPermissions.some(p => permission.permissions.includes(p));
  }

  /**
   * 檢查是否有所有權限
   *
   * @param permission 權限資料
   * @param requiredPermissions 需要的權限列表
   * @returns 是否有所有權限
   */
  hasAllPermissions(permission: ComponentPermissionData | null, requiredPermissions: string[]): boolean {
    if (!permission) return false;
    return requiredPermissions.every(p => permission.permissions.includes(p));
  }

  /**
   * 取得權限的中文說明
   *
   * @param permissionCode 權限代碼
   * @returns 中文說明
   */
  getPermissionLabel(permissionCode: string): string {
    const labels: { [key: string]: string } = {
      'view': '檢視',
      'create': '新增',
      'edit': '編輯',
      'delete': '刪除',
      'export': '匯出',
      'import': '匯入',
      'approve': '簽核',
    };
    return labels[permissionCode] || permissionCode;
  }

  /**
   * 取得 dataScope 的中文說明
   *
   * @param dataScope 資料範圍
   * @returns 中文說明
   */
  getDataScopeLabel(dataScope?: string): string {
    if (!dataScope) return '僅自己的資料';

    const labels: { [key: string]: string } = {
      'all': '所有資料',
      'department-below': '本部門及下屬部門',
      'department-self': '僅本部門',
      'own': '僅自己的資料'
    };

    return labels[dataScope] || dataScope;
  }

  /**
   * 取得呼叫者的元件名稱（透過 call stack）
   * 這是一個 fallback 方法，建議還是明確傳入 componentName
   */
  private getCallerComponentName(): string {
    const error = new Error();
    const stack = error.stack || '';
    const lines = stack.split('\n');

    // 嘗試從 stack trace 中找到元件名稱
    for (const line of lines) {
      if (line.includes('Component')) {
        const match = line.match(/(\w+Component)/);
        if (match) {
          return match[1];
        }
      }
    }

    return 'UnknownComponent';
  }
}
